import { useEffect, useState } from 'react'
import {
  holeMerkmale,
  type BildErkennung,
  type ErkannterTreffer,
  type Merkmal,
} from '../lib/warteschlangen'

/**
 * Was ein Bildmodell auf den Bildern sieht — und was davon an den Ort soll.
 *
 * **Das Modell schlägt vor, der Mensch entscheidet.** Ohne Haken wird nichts
 * geschrieben; die Haken gehen zusammen mit der Entscheidung hinaus, in einer
 * Transaktion.
 *
 * **Was nicht erkannt wurde, steht hier nicht.** Ein Foto belegt Anwesenheit,
 * nie Abwesenheit: „keine Bank zu sehen" ist eine Auskunft über den
 * Bildausschnitt, nicht über den Spielplatz. Aus einem Foto entsteht deshalb
 * nie ein „gibt es hier nicht".
 *
 * Drei Fälle, und der dritte ist der interessante:
 *
 * | schon bekannt | Darstellung | vorausgewählt |
 * |---|---|---|
 * | `true` | grau, zur Kenntnis | – |
 * | nicht hinterlegt | Kästchen | ab 80 % Sicherheit |
 * | ausdrücklich `false` | Kästchen, als Widerspruch | **nein** |
 *
 * Ein Widerspruch ist kein Fehler des Modells: Jemand hat einmal gesagt, das
 * gebe es hier nicht, und das Bild sagt etwas anderes. Beides kann stimmen —
 * zwischen dem Nein und dem Foto liegen vielleicht zwei Jahre.
 *
 * **Zwei Stellen, eine Karte.** An einem wartenden Foto ist „schon bekannt"
 * die Ausstattung des Ortes. An einer Einreichung für einen neuen Ort ist es,
 * was der Einreicher selbst angegeben hat — seine Angabe schlägt die
 * Erkennung, er war dort, das Modell hat ein Bild gesehen.
 */

/** Ab hier haken wir vor. Darunter sieht der Mensch selbst nach. */
const SCHWELLE = 0.8

type AmOrt = 'ja' | 'nein' | 'fehlt'

function bekannt(features: Record<string, unknown> | null, schluessel: string): AmOrt {
  const wert = features?.[schluessel]
  if (wert === true) return 'ja'
  if (wert === false) return 'nein'
  return 'fehlt'
}

/**
 * Mehrere Bilder, ein Vorschlag je Merkmal: Es zählt das Bild, auf dem das
 * Modell sich am sichersten war. Zwei Zeilen „Rutsche" wären keine zwei
 * Rutschen, sondern zweimal dieselbe.
 */
export function zusammengefasst(bilder: BildErkennung[]): ErkannterTreffer[] {
  const beste = new Map<string, ErkannterTreffer>()
  for (const b of bilder) {
    if (b.erkennung.zustand !== 'ok') continue
    for (const t of b.erkennung.erkannt) {
      const bisher = beste.get(t.schluessel)
      if (!bisher || bisher.sicherheit < t.sicherheit) beste.set(t.schluessel, t)
    }
  }
  return [...beste.values()].sort((a, b) => b.sicherheit - a.sicherheit)
}

export function vorauswahl(
  bilder: BildErkennung[],
  features: Record<string, unknown> | null,
): string[] {
  return zusammengefasst(bilder)
    .filter((t) => bekannt(features, t.schluessel) === 'fehlt' && t.sicherheit >= SCHWELLE)
    .map((t) => t.schluessel)
}

const prozent = (n: number) => `${Math.round(n * 100)} %`

/** Die Gruppen in der Reihenfolge des Katalogs der App. */
const GRUPPENNAME: Record<string, string> = {
  swings: 'Schaukeln',
  slides: 'Rutschen',
  play: 'Spielen',
  climbing: 'Klettern',
  sport: 'Sport',
  surface: 'Untergrund',
  umfeld: 'Umfeld',
}

/**
 * Der Katalog wird **einmal** geholt und dann behalten: Er ändert sich nur,
 * wenn die App sich ändert, und jede Einreichung erneut zu fragen wäre eine
 * Anfrage ohne Ertrag.
 */
let katalogSpeicher: Merkmal[] | null = null

function useKatalog(): Merkmal[] {
  const [merkmale, setMerkmale] = useState<Merkmal[]>(katalogSpeicher ?? [])
  useEffect(() => {
    if (katalogSpeicher) return
    let abgebrochen = false
    void holeMerkmale()
      .then((a) => {
        katalogSpeicher = a.merkmale
        if (!abgebrochen) setMerkmale(a.merkmale)
      })
      .catch(() => {
        // Ohne Katalog fehlt nur die Liste zum Selbstankreuzen; die
        // Vorschläge des Modells stehen trotzdem da.
      })
    return () => {
      abgebrochen = true
    }
  }, [])
  return merkmale
}

export default function ErkennungsKarte({
  bilder,
  features,
  umfeld = null,
  gewaehlt,
  setzen,
  nachladen,
  erneut,
  beiErgebnis,
  bekanntText = 'steht schon am Ort',
  widerspruchText = 'am Ort steht „gibt es hier nicht"',
}: {
  bilder: BildErkennung[]
  /** Was schon bekannt ist: die Ausstattung des Ortes oder die Angabe des Einreichers. */
  features: Record<string, unknown> | null
  /** Dasselbe für das Umfeld — es liegt in einer anderen Spalte. */
  umfeld?: Record<string, unknown> | null
  gewaehlt: string[]
  setzen: (schluessel: string[]) => void
  /** Holt den Stand neu, solange noch gerechnet wird. */
  nachladen: () => Promise<BildErkennung[]>
  /** Stößt einen neuen Lauf an. */
  erneut: () => Promise<void>
  /**
   * Meldet nachgeladene Ergebnisse nach oben.
   *
   * **Ohne das bliebe die Vorauswahl leer.** Diese Karte lädt selbst nach,
   * solange gerechnet wird, und behielt das Ergebnis für sich; wer darüber
   * die Häkchen vorbelegt, rechnete für immer mit dem Stand von damals —
   * und beim ersten Öffnen einer Seite ist das „noch nichts". Aufgefallen
   * am 29.09.2026 in der Ortsdurchsicht, wo jede erste Seite so aussieht.
   */
  beiErgebnis?: (bilder: BildErkennung[]) => void
  bekanntText?: string
  widerspruchText?: string
}) {
  const [stand, setStand] = useState<BildErkennung[]>(bilder)
  const [erneutLaeuft, setErneutLaeuft] = useState(false)
  const [alleZeigen, setAlleZeigen] = useState(false)
  const katalog = useKatalog()

  const schluessel = bilder.map((b) => b.pfad).join('|')
  useEffect(() => {
    setStand(bilder)
  }, [schluessel, bilder])

  const laeuft = stand.some((b) => b.erkennung.zustand === 'laeuft')
  const fehler = stand.filter((b) => b.erkennung.zustand === 'fehler')
  const treffer = zusammengefasst(stand)

  useEffect(() => {
    if (!laeuft) return
    let abgebrochen = false
    let versuche = 0

    const uhr = setInterval(() => {
      versuche += 1
      // Nach zwei Minuten aufhören zu fragen. Wer so lange nichts liefert,
      // liefert auch beim einundzwanzigsten Mal nichts.
      if (versuche > 40) {
        clearInterval(uhr)
        return
      }
      void nachladen()
        .then((neu) => {
          if (abgebrochen) return
          setStand(neu)
          if (!neu.some((b) => b.erkennung.zustand === 'laeuft')) {
            clearInterval(uhr)
            beiErgebnis?.(neu)
          }
        })
        .catch(() => {})
    }, 3000)

    return () => {
      abgebrochen = true
      clearInterval(uhr)
    }
  }, [schluessel, laeuft, nachladen, beiErgebnis])

  async function nochmal() {
    setErneutLaeuft(true)
    try {
      await erneut()
      setStand(
        stand.map((b) => ({
          ...b,
          erkennung: { zustand: 'laeuft', modell: null, erkannt: [], fehler: null },
        })),
      )
    } catch {
      // Der Knopf ist eine Bequemlichkeit; scheitert er, bleibt der Fehler
      // stehen, und der Vorgang lässt sich trotzdem entscheiden.
    } finally {
      setErneutLaeuft(false)
    }
  }

  if (bilder.length === 0) return null

  const modell = stand.find((b) => b.erkennung.modell)?.erkennung.modell ?? null

  return (
    <div className="karte">
      <h3>Was das Modell auf {bilder.length === 1 ? 'dem Bild' : 'den Bildern'} sieht</h3>

      {laeuft && (
        <p className="gedaempft">
          Wird analysiert … Entscheiden lässt sich auch jetzt schon.
        </p>
      )}

      {fehler.length > 0 && (
        <>
          <p className="meldung meldung-warnung">
            {fehler.length === stand.length
              ? 'Die Bilderkennung ist gescheitert: '
              : `Bei ${fehler.length} von ${stand.length} Bildern gescheitert: `}
            {fehler[0]?.erkennung.fehler ?? 'ohne Angabe'}
          </p>
          <p className="leise">
            Am Vorgang liegt es nicht — annehmen und ablehnen gehen unverändert.
          </p>
          <div className="knopf-reihe">
            <button
              className="knopf knopf-still"
              type="button"
              disabled={erneutLaeuft}
              onClick={() => void nochmal()}
            >
              Erneut versuchen
            </button>
          </div>
        </>
      )}

      {!laeuft && fehler.length === 0 && treffer.length === 0 && (
        <p className="gedaempft">
          Nichts erkannt, was im Merkmalskatalog steht. Das heißt nicht, dass es
          dort nichts gibt — nur, dass auf {bilder.length === 1 ? 'dem Bild' : 'den Bildern'}{' '}
          nichts zu sehen war.
        </p>
      )}

      {treffer.length > 0 && (
        <>
          <ul className="erkannt-liste">
            {treffer.map((t) => (
              <Zeile
                key={t.schluessel}
                treffer={t}
                lage={bekannt(features, t.schluessel)}
                bekanntText={bekanntText}
                widerspruchText={widerspruchText}
                gewaehlt={gewaehlt.includes(t.schluessel)}
                umschalten={(an) =>
                  setzen(
                    an
                      ? [...new Set([...gewaehlt, t.schluessel])]
                      : gewaehlt.filter((g) => g !== t.schluessel),
                  )
                }
              />
            ))}
          </ul>

          <p className="leise gesehen-zeile">
            Angehakt wird zusammen mit der Entscheidung geschrieben. Aus einem
            Foto entsteht nie ein „gibt es hier nicht".
            {modell && <span className="zahl"> · {modell}</span>}
          </p>
        </>
      )}

      {/* Das Modell übersieht Dinge — ein halb verdecktes Gerät, etwas am
          Bildrand. Wer es sieht, soll es anhaken können, ohne den Umweg über
          eine zweite Warteschlange. */}
      <div className="alle-merkmale">
        <button
          className="knopf knopf-still"
          type="button"
          onClick={() => setAlleZeigen((a) => !a)}
          aria-expanded={alleZeigen}
        >
          {alleZeigen ? 'Weitere Merkmale ausblenden' : 'Weitere Merkmale selbst anhaken …'}
        </button>

        {alleZeigen && katalog.length === 0 && (
          <p className="leise">Der Merkmalskatalog ließ sich nicht laden.</p>
        )}

        {alleZeigen && (
          <p className="leise">
            Das Modell nennt nur Geräte und Bodenbeläge. Umfeld — Bänke,
            Toiletten, Parkplätze, Zaun — sieht man auf einem Bild selten
            sicher; das hakt ein Mensch an oder niemand.
          </p>
        )}

        {alleZeigen &&
          Object.entries(
            katalog
              .filter((m) => !treffer.some((t) => t.schluessel === m.schluessel))
              .reduce<Record<string, Merkmal[]>>((gruppen, m) => {
                ;(gruppen[m.gruppe] ??= []).push(m)
                return gruppen
              }, {}),
          ).map(([gruppe, liste]) => (
            <div key={gruppe} className="merkmalsgruppe">
              <p className="leise">{GRUPPENNAME[gruppe] ?? gruppe}</p>
              <div className="merkmalsliste">
                {liste.map((m) => {
                  // Je Spalte nachsehen: Eine Bank steht in `accessibility`,
                  // eine Rutsche in `features`.
                  const lage = bekannt(
                    m.speicher === 'accessibility' ? umfeld : features,
                    m.schluessel,
                  )
                  return (
                    <label
                      key={m.schluessel}
                      className={lage === 'ja' ? 'merkmal bekannt' : 'merkmal'}
                    >
                      <input
                        type="checkbox"
                        checked={lage === 'ja' || gewaehlt.includes(m.schluessel)}
                        disabled={lage === 'ja'}
                        onChange={(ev) =>
                          setzen(
                            ev.target.checked
                              ? [...new Set([...gewaehlt, m.schluessel])]
                              : gewaehlt.filter((g) => g !== m.schluessel),
                          )
                        }
                      />
                      <span>
                        {m.bezeichnung}
                        {lage === 'nein' && (
                          <span className="leise"> · steht auf „gibt es nicht"</span>
                        )}
                      </span>
                    </label>
                  )
                })}
              </div>
            </div>
          ))}
      </div>
    </div>
  )
}

function Zeile({
  treffer,
  lage,
  gewaehlt,
  umschalten,
  bekanntText,
  widerspruchText,
}: {
  treffer: ErkannterTreffer
  lage: AmOrt
  gewaehlt: boolean
  umschalten: (an: boolean) => void
  bekanntText: string
  widerspruchText: string
}) {
  if (lage === 'ja') {
    return (
      <li className="erkannt erkannt-bekannt">
        <span className="erkannt-haken" aria-hidden="true">
          ✓
        </span>
        <span className="erkannt-text">
          <span>{treffer.bezeichnung}</span>
          <span className="leise">
            {bekanntText} · {prozent(treffer.sicherheit)}
            {treffer.beleg && ` · ${treffer.beleg}`}
          </span>
        </span>
      </li>
    )
  }

  return (
    <li className={lage === 'nein' ? 'erkannt erkannt-widerspruch' : 'erkannt'}>
      <label>
        <input
          type="checkbox"
          checked={gewaehlt}
          onChange={(e) => umschalten(e.target.checked)}
        />
        <span className="erkannt-text">
          <span>
            {treffer.bezeichnung}
            {lage === 'nein' && <strong className="wort-ab"> · Widerspruch</strong>}
          </span>
          <span className="leise">
            {lage === 'nein' ? `${widerspruchText} · ` : 'noch nicht hinterlegt · '}
            {prozent(treffer.sicherheit)}
            {treffer.beleg && ` · ${treffer.beleg}`}
          </span>
        </span>
      </label>
    </li>
  )
}
